import {
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import PaymentMethods from "./PaymentMethods";
import RequestPaymentMethodForm from "./RequestPaymentMethodForm";
import {
  Auth0AccountRole,
  ParentLimitedResponse,
  StudentLimitedResponse,
} from "client/openapi";
import { useContext, useEffect, useState } from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import Notifications from "util/notifications";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { APIResponse } from "types";

export default function PaymentInformationModal({
  studentIfInitiatedByAdmin,
  parentIfInitiatedByAdmin,
  initiatedByStudentOrParentDirectly,
  orgStripeAccountId,
}: {
  studentIfInitiatedByAdmin?: StudentLimitedResponse;
  parentIfInitiatedByAdmin?: ParentLimitedResponse;
  initiatedByStudentOrParentDirectly?: boolean;
  orgStripeAccountId?: string;
}) {
  const { currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);

  const [
    isRequestingPaymentMethodViaText,
    setIsRequestingPaymentMethodViaText,
  ] = useState(false);
  const [addCardOrBankManually, setAddCardOrBankManually] =
    useState<boolean>(false);

  const stripe = useStripe();
  const elements = useElements();

  const [error, setError] = useState<APIResponse>();

  const reset = () => setIsRequestingPaymentMethodViaText(false);

  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null;
    }

    const { error } = await stripe.confirmSetup({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: studentIfInitiatedByAdmin
          ? `${
              process.env.REACT_APP_FRONTEND_DOMAIN
            }/manage/students/payment-status?user_id=${
              studentIfInitiatedByAdmin.id
            }${
              orgStripeAccountId
                ? `&org_stripe_account_id=${orgStripeAccountId}`
                : ""
            }`
          : parentIfInitiatedByAdmin
          ? `${
              process.env.REACT_APP_FRONTEND_DOMAIN
            }/manage/parents/payment-status?user_id=${
              parentIfInitiatedByAdmin.id
            }${
              orgStripeAccountId
                ? `&org_stripe_account_id=${orgStripeAccountId}`
                : ""
            }`
          : `${process.env.REACT_APP_FRONTEND_DOMAIN}/dashboard/payment-status`,
      },
    });

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setError(error);
    } else {
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
  };

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  return (
    <DialogContent className="dialog-content max-w-[800px]" onClose={reset}>
      <div className="mt-3 font-semibold text-center header text-lg">
        Payment Information
      </div>

      <div className="border rounded-lg border-gray-400 px-6 py-4 mt-6">
        <PaymentMethods
          deletable={true}
          studentIfInitiatedByAdmin={studentIfInitiatedByAdmin}
          parentIfInitiatedByAdmin={parentIfInitiatedByAdmin}
          initiatedByStudentOrParentDirectly={
            initiatedByStudentOrParentDirectly
          }
        />

        {currently_selected_role === Auth0AccountRole.ORG_ADMIN && (
          <>
            <hr className="mt-6 mb-3 border-gray-300" />

            {!isRequestingPaymentMethodViaText ? (
              <div className="mt-4">
                <Button
                  color={ButtonColor.GREEN}
                  onClick={() => setIsRequestingPaymentMethodViaText(true)}
                >
                  Request Payment Method via Text/Email
                </Button>
              </div>
            ) : (
              <RequestPaymentMethodForm
                orgId={currently_selected_organization as number}
                student={studentIfInitiatedByAdmin}
                parent={parentIfInitiatedByAdmin}
              />
            )}

            <hr className="mt-6 mb-3 border-gray-300" />
          </>
        )}

        {!addCardOrBankManually && (
          <button
            className={`mt-4 underline text-xs font-light header font-semibold ${
              currently_selected_role === Auth0AccountRole.ORG_ADMIN
                ? "text-gray-600"
                : "text-blue-600"
            }`}
            onClick={() => setAddCardOrBankManually(true)}
          >
            {currently_selected_role === Auth0AccountRole.ORG_ADMIN
              ? "or, add new card or bank manually"
              : "add new card or bank"}
          </button>
        )}

        {addCardOrBankManually && (
          <>
            <form
              onSubmit={handleSubmit}
              className="rounded border mt-4 p-4 border-gray-300"
            >
              <PaymentElement
                onLoadError={({ error }) =>
                  Notifications.error(
                    error.message ||
                      "The payment form could not load. Please try again."
                  )
                }
              />
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <Button
                  disabled={!stripe}
                  color={ButtonColor.GREEN}
                  size={ButtonSize.DEFAULT}
                  style={{ marginRight: "10px" }}
                >
                  Add
                </Button>
                <Button
                  color={ButtonColor.PURPLE}
                  size={ButtonSize.DEFAULT}
                  fill={ButtonFill.HOLLOW}
                  onClick={() => setAddCardOrBankManually(false)}
                >
                  Cancel
                </Button>
              </div>

              {/* Show error message to your customers */}
              {error && <div>{error.message}</div>}
            </form>
          </>
        )}
      </div>

      <DialogActions>
        <DialogClose asChild>
          <DialogAction color={ButtonColor.PURPLE}>Close</DialogAction>
        </DialogClose>
      </DialogActions>
    </DialogContent>
  );
}
